import React, { createContext, useContext, useState, useEffect } from 'react';

const HealthContext = createContext();
const API = process.env.REACT_APP_BACKEND_URL;

export const useHealth = () => {
  const context = useContext(HealthContext);
  if (!context) {
    throw new Error('useHealth must be used within a HealthProvider');
  }
  return context;
};

export const HealthProvider = ({ children }) => {
  const [treatments, setTreatments] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [products, setProducts] = useState([]);
  const [veterinarians, setVeterinarians] = useState([]);

  // Initialize veterinarians and products (can be moved to backend later)
  useEffect(() => {
    const savedProducts = localStorage.getItem('products');
    const savedVets = localStorage.getItem('veterinarians');
    
    if (savedProducts) {
      setProducts(JSON.parse(savedProducts));
    } else {
      const mockProducts = [
        { id: 'p1', name: 'Ivermectina Plus', type: 'Desparasitante', unitPrice: 45.00, unit: 'ml' },
        { id: 'p2', name: 'Complejo B + ADE', type: 'Vitamina', unitPrice: 25.00, unit: 'ml' },
        { id: 'p3', name: 'Triple Viral', type: 'Vacuna', unitPrice: 60.00, unit: 'dosis' },
        { id: 'p4', name: 'Oxitetraciclina LA', type: 'Antibiótico', unitPrice: 85.00, unit: 'ml' },
        { id: 'p5', name: 'Antiinflamatorio', type: 'AINE', unitPrice: 40.00, unit: 'ml' }
      ];
      setProducts(mockProducts);
      localStorage.setItem('products', JSON.stringify(mockProducts));
    }

    if (savedVets) {
      setVeterinarians(JSON.parse(savedVets));
    } else {
      const mockVets = [
        { id: 'v1', name: 'Dr. Carlos Martínez', specialty: 'Bovinos', phone: '555-0101', license: 'VET-12345' },
        { id: 'v2', name: 'Dr. Ana López', specialty: 'Medicina General', phone: '555-0102', license: 'VET-67890' },
        { id: 'v3', name: 'Juan Pérez', role: 'Técnico Ganadero', phone: '555-0103' },
        { id: 'v4', name: 'María González', role: 'Técnico Veterinario', phone: '555-0104' }
      ];
      setVeterinarians(mockVets);
      localStorage.setItem('veterinarians', JSON.stringify(mockVets));
    }
  }, []);

  // Treatment functions
  const addTreatment = async (treatmentData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${API}/api/cattle/${treatmentData.cattleId}/treatments`, {
        method: 'POST',
        headers,
        body: JSON.stringify(treatmentData)
      });

      if (!response.ok) throw new Error('Error al agregar tratamiento');

      const newTreatment = await response.json();
      setTreatments(prev => [...prev, newTreatment]);
      return newTreatment;
    } catch (error) {
      console.error('Error al agregar tratamiento:', error);
      throw error;
    }
  };

  const updateTreatment = (id, treatmentData) => {
    setTreatments(prev => prev.map(t => t.id === id ? { ...t, ...treatmentData } : t));
  };

  const deleteTreatment = (id) => {
    setTreatments(prev => prev.filter(t => t.id !== id));
  };

  const getTreatmentsByCattle = async (cattleId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await fetch(`${API}/api/cattle/${cattleId}/treatments`, { headers });
      if (!response.ok) throw new Error('Error al obtener tratamientos');

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al obtener tratamientos:', error);
      return [];
    }
  };

  const getUpcomingTreatments = (daysAhead = 30) => {
    const today = new Date();
    const futureDate = new Date(today.getTime() + daysAhead * 24 * 60 * 60 * 1000);
    
    return treatments.filter(t => {
      if (!t.nextApplicationDate) return false;
      const nextDate = new Date(t.nextApplicationDate);
      return nextDate >= today && nextDate <= futureDate;
    }).sort((a, b) => new Date(a.nextApplicationDate) - new Date(b.nextApplicationDate));
  };

  // Diagnosis functions
  const addDiagnosis = async (diagnosisData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${API}/api/cattle/${diagnosisData.cattleId}/diagnoses`, {
        method: 'POST',
        headers,
        body: JSON.stringify(diagnosisData)
      });

      if (!response.ok) throw new Error('Error al agregar diagnóstico');

      const newDiagnosis = await response.json();
      setDiagnoses(prev => [...prev, newDiagnosis]);
      return newDiagnosis;
    } catch (error) {
      console.error('Error al agregar diagnóstico:', error);
      throw error;
    }
  };

  const updateDiagnosis = (id, diagnosisData) => {
    setDiagnoses(prev => prev.map(d => d.id === id ? { ...d, ...diagnosisData } : d));
  };

  const deleteDiagnosis = (id) => {
    setDiagnoses(prev => prev.filter(d => d.id !== id));
  };

  const getDiagnosesByCattle = async (cattleId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await fetch(`${API}/api/cattle/${cattleId}/diagnoses`, { headers });
      if (!response.ok) throw new Error('Error al obtener diagnósticos');

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al obtener diagnósticos:', error);
      return [];
    }
  };

  // Cost calculations (will need to fetch from backend if needed)
  const getTotalCostByCattle = async (cattleId) => {
    const cattleTreatments = await getTreatmentsByCattle(cattleId);
    const cattleDiagnoses = await getDiagnosesByCattle(cattleId);
    
    const treatmentCosts = cattleTreatments.reduce((sum, t) => sum + (parseFloat(t.cost) || 0), 0);
    const diagnosisCosts = cattleDiagnoses.reduce((sum, d) => sum + (parseFloat(d.cost) || 0), 0);
    
    return treatmentCosts + diagnosisCosts;
  };

  const getTotalHealthCosts = () => {
    const treatmentCosts = treatments.reduce((sum, t) => sum + (parseFloat(t.cost) || 0), 0);
    const diagnosisCosts = diagnoses.reduce((sum, d) => sum + (parseFloat(d.cost) || 0), 0);
    return treatmentCosts + diagnosisCosts;
  };

  // Product functions
  const addProduct = (productData) => {
    const newProduct = {
      ...productData,
      id: Date.now().toString()
    };
    setProducts(prev => {
      const updated = [...prev, newProduct];
      localStorage.setItem('products', JSON.stringify(updated));
      return updated;
    });
    return newProduct;
  };

  const value = {
    treatments,
    diagnoses,
    products,
    veterinarians,
    addTreatment,
    updateTreatment,
    deleteTreatment,
    getTreatmentsByCattle,
    getUpcomingTreatments,
    addDiagnosis,
    updateDiagnosis,
    deleteDiagnosis,
    getDiagnosesByCattle,
    getTotalCostByCattle,
    getTotalHealthCosts,
    addProduct
  };

  return (
    <HealthContext.Provider value={value}>
      {children}
    </HealthContext.Provider>
  );
};
